import React, { useState, useEffect, useCallback } from 'react';
import { useGames } from '../gamesStore';
import API from '../api';

function logoUrl(abbr) {
  return 'https://a.espncdn.com/i/teamlogos/nfl/500/' + abbr.toLowerCase() + '.png';
}

function TeamLogo({ abbr, size, dim }) {
  const [ok, setOk] = useState(true);
  const opacity = dim ? 0.35 : 1;
  return ok ? (
    <img
      src={logoUrl(abbr)}
      alt={abbr}
      width={size}
      height={size}
      style={{ objectFit: 'contain', opacity, transition: 'opacity 0.2s', flexShrink: 0 }}
      onError={() => setOk(false)}
    />
  ) : (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      background: '#1e2a3a', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: size * 0.35, fontWeight: 700,
      color: dim ? '#445' : '#778', flexShrink: 0, opacity
    }}>{abbr.substring(0, 2)}</div>
  );
}

function GameChip({ game }) {
  const isFinal = game.status === 'final';
  const isLive = game.status === 'in_progress';
  const showScore = isFinal || isLive;
  const awayWins = isFinal && game.away_score > game.home_score;
  const homeWins = isFinal && game.home_score > game.away_score;

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '0 14px', height: 42,
      borderRight: '1px solid rgba(255,255,255,0.05)', flexShrink: 0
    }}>
      {/* Away */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <TeamLogo abbr={game.away_abbr} size={22} dim={isFinal && !awayWins} />
        <span style={{
          fontFamily: "'Oswald', sans-serif", fontSize: 13, fontWeight: 700,
          color: isFinal && !awayWins ? '#445' : '#ccd'
        }}>{game.away_abbr}</span>
        {showScore && (
          <span style={{
            fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700,
            color: awayWins ? '#fff' : '#667', minWidth: 18, textAlign: 'right'
          }}>{game.away_score}</span>
        )}
      </div>

      {/* Middle */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 62 }}>
        {isLive ? (
          // "LIVE" alone said a game was on but not how far through, which is
          // the first thing anyone wants at a glance. status_detail is ESPN's
          // own string — "5:42 - 3rd", "Halftime", "2nd OT" — so every odd state
          // reads correctly without us guessing at it. Older rows and any game
          // ESPN hasn't given a clock for fall back to the bare badge.
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              background: 'rgba(213,10,10,0.3)', color: '#ff6060', letterSpacing: 0.5
            }}>LIVE</span>
            {game.status_detail && (
              <span style={{ fontSize: 9, color: '#ff9d9d', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {game.status_detail}
              </span>
            )}
          </span>
        ) : isFinal ? (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
            background: 'rgba(76,175,80,0.18)', color: '#66bb6a', letterSpacing: 0.5
          }}>FINAL</span>
        ) : (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25 }}>
            {game.game_date && (
              <span style={{ fontSize: 9, color: '#3a4560', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {game.game_date}
              </span>
            )}
            <span style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
              {game.game_time}
            </span>
          </span>
        )}
        {!showScore && (
          <span style={{ fontSize: 9, color: '#334', letterSpacing: 0.5 }}>@</span>
        )}
      </div>

      {/* Home */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {showScore && (
          <span style={{
            fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700,
            color: homeWins ? '#fff' : '#667', minWidth: 18, textAlign: 'left'
          }}>{game.home_score}</span>
        )}
        <span style={{
          fontFamily: "'Oswald', sans-serif", fontSize: 13, fontWeight: 700,
          color: isFinal && !homeWins ? '#445' : '#ccd'
        }}>{game.home_abbr}</span>
        <TeamLogo abbr={game.home_abbr} size={22} dim={isFinal && !homeWins} />
      </div>
    </div>
  );
}

const POSTSEASON_ROUNDS = {
  1: 'WILD CARD', 2: 'DIVISIONAL', 3: 'CONF CHAMP', 4: 'PRO BOWL', 5: 'SUPER BOWL'
};

/**
 * Compact week name for the ticker pill, e.g. "PRESEASON WK 2" / "WEEK 5" /
 * "SUPER BOWL". Built from season_type + week so it doesn't depend on parsing
 * the stored label, and it follows the sync automatically.
 */
function weekPill(week) {
  if (!week) return 'NFL';
  const type = week.season_type == null ? 2 : week.season_type;
  const n = week.week;
  if (type === 1) return `PRESEASON WK ${n}`;
  if (type === 3) return POSTSEASON_ROUNDS[n] || `PLAYOFFS WK ${n}`;
  return `WEEK ${n}`;
}

export default function ScoreTicker() {
  // The same copy the picks cards read. Matching the poll rate wasn't enough:
  // two timers started at different moments still land at different moments, so
  // the clock up here and the clock on the card below could differ by a quarter
  // of a minute. One fetch, one copy, no drift.
  const { games, week } = useGames();

  if (games.length === 0) return null;

  const hasLive = games.some(g => g.status === 'in_progress');
  const shouldScroll = games.length > 3;
  const label = weekPill(week);

  return (
    <div style={{
      background: '#060b16',
      borderBottom: '1px solid rgba(255,255,255,0.07)',
      height: 42,
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      position: 'sticky',
      top: 56,
      zIndex: 98,
    }}>
      {/* Label pill */}
      <div style={{
        padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)',
        height: '100%', display: 'flex', alignItems: 'center', gap: 6,
        flexShrink: 0, background: 'rgba(0,0,0,0.3)'
      }}>
        {hasLive && (
          <span style={{
            width: 7, height: 7, borderRadius: '50%', background: '#D50A0A',
            display: 'inline-block', flexShrink: 0,
            animation: 'tickerDot 1.2s ease-in-out infinite'
          }} />
        )}
        {hasLive && (
          <span style={{
            fontFamily: "'Oswald', sans-serif", fontSize: 11, fontWeight: 700,
            color: '#D50A0A', letterSpacing: 1.2, whiteSpace: 'nowrap'
          }}>LIVE</span>
        )}
        {/* Which week we're in — follows the ESPN sync automatically */}
        <span style={{
          fontFamily: "'Oswald', sans-serif", fontSize: 11, fontWeight: 700,
          color: hasLive ? '#667' : '#7fb3ff', letterSpacing: 1.2, whiteSpace: 'nowrap'
        }}>{label}</span>
      </div>

      {/* Scrolling area */}
      <div style={{ flex: 1, overflow: 'hidden', height: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          animation: shouldScroll ? 'tickerMove ' + (games.length * 6) + 's linear infinite' : 'none',
          willChange: 'transform'
        }}>
          {games.map(g => <GameChip key={g.id} game={g} />)}
          {/* Seamless duplicate */}
          {shouldScroll && games.map(g => <GameChip key={'b-' + g.id} game={g} />)}
        </div>
      </div>

      <style>{`
        @keyframes tickerDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.25; transform: scale(0.7); }
        }
        @keyframes tickerMove {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

export { TeamLogo, logoUrl };
